import { ReloadOutlined } from '@ant-design/icons';
import type { ApprovalTaskRow, PriceChangeRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayDate, formatDisplayTime } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Descriptions, Drawer, Input, Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hq } from '../api/client';
import ApprovalTracePanel from '../components/ApprovalTracePanel';
import { ApplyStatusTag } from '../components/StatusTag';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

/** 业务类型 → 展示与详情路由（docs/03 §审批中心：待办按 bizType 分流）。 */
const BIZ_TYPE_META: Record<string, { text: string; color: string }> = {
  WL_APPLY: { text: '物资申领', color: 'blue' },
  WL_PRICE_CHANGE: { text: '调价申请', color: 'purple' }
};

/**
 * 审批中心：待我审批（todo）/ 我已审批（done）。
 * 申领单跳 `/apply/:id` 处理；调价申请（`WL_PRICE_CHANGE`）在抽屉内直接批阅。
 */
export default function ApprovalList({ mode }: { mode: 'todo' | 'done' }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();
  const done = mode === 'done';

  const [priceId, setPriceId] = useState<string | null>(null);
  const [opinion, setOpinion] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [batchOpinion, setBatchOpinion] = useState('');

  const query = useInfiniteQuery({
    queryKey: ['approval', mode],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      done
        ? hq.approval.done({ lastId: pageParam, size: PAGE_SIZE })
        : hq.approval.todo({ lastId: pageParam, size: PAGE_SIZE }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const priceDetail = useQuery({
    queryKey: ['price-change', 'detail', priceId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.priceChange.detail(priceId ?? ''),
    enabled: Boolean(priceId)
  });

  const decide = useMutation({
    mutationFn: (action: 'APPROVE' | 'REJECT') => {
      if (action === 'REJECT' && opinion.trim().length === 0) {
        return Promise.reject(new Error('驳回必须填写意见'));
      }
      return hq.priceChange.decide(priceId ?? '', { action, opinion: opinion.trim() || undefined });
    },
    onSuccess: async (result, action) => {
      message.success(
        action === 'APPROVE'
          ? `已同意${result.approvalStatus === 'APPROVED' ? '，调价已生效' : result.nodeName ? `，流转至「${result.nodeName}」` : ''}`
          : '已驳回'
      );
      setPriceId(null);
      setOpinion('');
      await queryClient.invalidateQueries({ queryKey: ['approval'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '审批失败')
  });

  /**
   * 表格 rowKey 是 taskId（审批任务 ID），批量审批接口要的是 applyId（业务单据 ID），
   * 两者是各自独立的雪花 ID，不能互换，这里按当前已加载行做一次映射。
   */
  const selectedApplyIds = (): string[] => {
    const byTaskId = new Map(rows.map((row) => [row.taskId, row.applyId]));
    return selectedIds.map((taskId) => byTaskId.get(taskId)).filter((id): id is string => Boolean(id));
  };

  // 批量审批：仅申领单（WL_APPLY），后端按流程 support_batch_approve 逐张校验
  const batchApprove = useMutation({
    mutationFn: (action: 'APPROVE' | 'REJECT') =>
      hq.approval.batchApprove({
        applyIds: selectedApplyIds(),
        action,
        opinion: batchOpinion.trim() || undefined
      }),
    onSuccess: async (data, action) => {
      const text = action === 'APPROVE' ? '同意' : '驳回';
      if (data.failed === 0) {
        message.success(`批量${text}成功 ${data.succeeded} 张`);
      } else {
        const failedItems = data.items as Array<{ applyId: string; success: boolean; error?: string }>;
        const firstError = failedItems.find((item) => !item.success)?.error;
        message.warning(`成功 ${data.succeeded} 张，失败 ${data.failed} 张${firstError ? `：${firstError}` : ''}`);
      }
      setSelectedIds([]);
      setBatchOpinion('');
      await queryClient.invalidateQueries({ queryKey: ['approval'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '批量审批失败')
  });

  const runBatch = (action: 'APPROVE' | 'REJECT') => {
    if (selectedIds.length === 0) {
      message.warning('请先勾选待审批的申领单');
      return;
    }
    if (selectedIds.length > 20) {
      message.warning('单次批量审批不超过 20 张，请减少勾选数量');
      return;
    }
    if (action === 'REJECT' && batchOpinion.trim().length === 0) {
      message.warning('批量驳回必须填写意见');
      return;
    }
    modal.confirm({
      title: `批量${action === 'APPROVE' ? '同意' : '驳回'} ${selectedIds.length} 张申领单？`,
      content: '逐张按各自流程校验；未开启批量审批的流程会返回失败原因，不影响其他单据。',
      okText: '确认',
      cancelText: '取消',
      onOk: () => batchApprove.mutateAsync(action)
    });
  };

  /** 按 bizType 分流：申领单走详情页，调价申请在当前页弹抽屉。 */
  const openRow = (row: ApprovalTaskRow) => {
    if (row.bizType === 'WL_PRICE_CHANGE') {
      setOpinion('');
      setPriceId(row.applyId);
      return;
    }
    navigate(`/apply/${row.applyId}`);
  };

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<ApprovalTaskRow> = [
    {
      title: '单号',
      dataIndex: 'bizNo',
      width: 210,
      render: (value: string, row) => <Typography.Link onClick={() => openRow(row)}>{value}</Typography.Link>
    },
    {
      title: '类型',
      dataIndex: 'bizType',
      width: 110,
      render: (value: string) => {
        const meta = BIZ_TYPE_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '标题', dataIndex: 'applyTitle', ellipsis: { showTitle: false }, render: ellipsisCell('applyTitle') },
    { title: '申请科室', dataIndex: 'deptName', width: 140, render: (value: string | null) => value || '-' },
    { title: '申请人', dataIndex: 'startUserName', width: 110, render: (value: string | null) => value || '-' },
    {
      title: '金额',
      dataIndex: 'totalAmount',
      width: 130,
      align: 'right',
      render: (value: number) => (Number(value) > 0 ? formatAmount(value) : '-')
    },
    {
      title: '单据状态',
      dataIndex: 'bizStatus',
      width: 110,
      render: (value: ApprovalTaskRow['bizStatus']) => <ApplyStatusTag status={value} />
    },
    { title: '审批节点', dataIndex: 'nodeName', width: 120, render: (value: string | null) => value || '-' },
    {
      title: done ? '处理时间' : '提交时间',
      dataIndex: done ? 'handleAt' : 'startAt',
      width: 160,
      render: (_value: unknown, row) => formatDisplayTime(done ? row.handleAt : row.startAt)
    }
  ];

  if (done) {
    columns.push({
      title: '处理结果',
      dataIndex: 'action',
      width: 110,
      render: (value: string | null) =>
        value === 'APPROVE' ? <Tag color="green">同意</Tag> : value === 'REJECT' ? <Tag color="red">驳回</Tag> : <Tag>{value ?? '-'}</Tag>
    });
    columns.push({
      title: '意见',
      dataIndex: 'opinion',
      ellipsis: { showTitle: false },
      render: ellipsisCell('opinion')
    });
  } else {
    columns.push({
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_value: unknown, row) => (
        <Button type="link" onClick={() => openRow(row)}>
          处理
        </Button>
      )
    });
  }

  const price: PriceChangeRow | undefined = priceDetail.data?.price;

  return (
    <Card
      className="page-card"
      title={done ? '我已审批' : '待我审批'}
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
      }
    >
      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="审批列表加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      {!done ? (
        <Space wrap style={{ marginBottom: 12 }}>
          <Typography.Text type="secondary">已选 {selectedIds.length} 张（仅申领单支持批量）</Typography.Text>
          <Input
            style={{ width: 280 }}
            maxLength={500}
            value={batchOpinion}
            onChange={(event) => setBatchOpinion(event.target.value)}
            placeholder="批量审批意见（驳回必填）"
          />
          <Button
            type="primary"
            disabled={selectedIds.length === 0}
            loading={batchApprove.isPending}
            onClick={() => runBatch('APPROVE')}
          >
            批量同意
          </Button>
          <Button
            danger
            disabled={selectedIds.length === 0}
            loading={batchApprove.isPending}
            onClick={() => runBatch('REJECT')}
          >
            批量驳回
          </Button>
          {selectedIds.length > 0 ? (
            <Button type="link" onClick={() => setSelectedIds([])}>
              清空选择
            </Button>
          ) : null}
        </Space>
      ) : null}

      <Table<ApprovalTaskRow>
        rowKey="taskId"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : done ? '暂无已办记录' : '暂无待办' }}
        onRow={(row) => ({ onDoubleClick: () => openRow(row) })}
        rowSelection={
          done
            ? undefined
            : {
                selectedRowKeys: selectedIds,
                onChange: (keys) => setSelectedIds(keys as string[]),
                getCheckboxProps: (row) => ({ disabled: row.bizType !== 'WL_APPLY' })
              }
        }
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : rows.length > 0 ? (
          <Typography.Text type="secondary">没有更多了</Typography.Text>
        ) : null}
      </Space>

      <Drawer
        title={price?.changeNo ?? '调价申请'}
        open={priceId !== null}
        onClose={() => setPriceId(null)}
        width={720}
        extra={
          price?.approvalStatus === 'APPROVING' && !done ? (
            <Space>
              <Button type="primary" loading={decide.isPending} onClick={() => decide.mutate('APPROVE')}>
                同意
              </Button>
              <Button danger loading={decide.isPending} onClick={() => decide.mutate('REJECT')}>
                驳回
              </Button>
            </Space>
          ) : null
        }
      >
        {priceDetail.isLoading ? (
          <Spin />
        ) : priceDetail.isError ? (
          <Alert
            type="error"
            showIcon
            message="调价详情加载失败"
            description={priceDetail.error instanceof ApiError ? priceDetail.error.friendlyMessage : String(priceDetail.error)}
          />
        ) : price ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="状态">
                <Tag color={price.approvalStatus === 'APPROVED' ? 'green' : price.approvalStatus === 'REJECTED' ? 'red' : 'processing'}>
                  {price.approvalStatus}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="物资">
                {price.materialName ?? price.materialId}（{price.materialCode ?? '-'}）
              </Descriptions.Item>
              <Descriptions.Item label="规格">{price.spec || '-'}</Descriptions.Item>
              <Descriptions.Item label="供应商">{price.supplierId}</Descriptions.Item>
              <Descriptions.Item label="新单价">{formatAmount(price.price, 4)}</Descriptions.Item>
              <Descriptions.Item label="税率">{(Number(price.taxRate) * 100).toFixed(0)}%</Descriptions.Item>
              <Descriptions.Item label="生效日">{formatDisplayDate(price.effectiveFrom)}</Descriptions.Item>
              <Descriptions.Item label="提前天数">{price.noticeDays} 天</Descriptions.Item>
              <Descriptions.Item label="调价原因" span={2}>
                {price.changeReason || '-'}
              </Descriptions.Item>
            </Descriptions>

            {price.approvalStatus === 'APPROVING' && !done ? (
              <Input.TextArea
                rows={3}
                maxLength={500}
                value={opinion}
                onChange={(event) => setOpinion(event.target.value)}
                placeholder="审批意见（驳回必填）"
              />
            ) : null}

            <div style={{ marginTop: 16 }}>
              <ApprovalTracePanel
                instanceId={price.instanceId}
                onChanged={async () => {
                  await query.refetch();
                }}
              />
            </div>
          </>
        ) : null}
      </Drawer>
    </Card>
  );
}
